<template>
<block-wrap :adaptive="eAdaptive"
            :full-width="!inlineBlock"
            :as-transparent="transparent"
            :highlight="properties.highlight"
            :inner-wrap="properties.innerWrap"
            :adaptive-start="adaptiveStart"
            :adaptive-end="adaptiveEnd"
            :take-width="takeWidth"
            :parent-take-width="parentTakeWidth"
            :step-fields-count="stepFieldsCount">
    <div v-if="properties.highlight"
         class="odr-block__mark"
         :class="{'odr-block__mark_down': ['radio', 'checkboxList', 'checkbox'].includes(type)}">
        <span v-if="properties.required">
            <span v-if="!model" :key="model">
                <icon color="error">arrow_forward</icon>
            </span>
            <span v-if="model" :key="model">
                <icon color="valid">done</icon>
            </span>
        </span>
        <icon v-else color="gray">arrow_forward</icon>
    </div>
    <div v-else-if="properties.required &&
             showRequiredMark &&
             $root.appConfig.showRequiredMark &&
             hasTitle &&
             ['combobox', 'input', 'textarea', 'checkbox', 'checkboxList', 'radio', 'signature', 'time', 'uploadImage', 'uploadFile', 'datesimple', 'maskedDate', 'maskedInput'].includes(type)"
         class="odr-block__small-mark"
         :class="{
             'odr-block__small-mark_not-as-field': !asField,
             'odr-block__small-mark_adaptive': adaptive || properties.innerWrap
         }">
        <text-regular minor="true" color="error">*</text-regular>
    </div>
    <div v-if="hasDropdown"
         :class="{
             'odr-block__wrap': hasFieldWrap,
             'odr-block__wrap_dropdown': hasFieldWrap,
         }">
        <dropdown :id="dataQa"
                  ref="dropdown"
                  :shift="dropdownShift">
            <text-regular color="error" v-if="$store.state.local.showBlockIds">Block id: {{id}}</text-regular>

            <text-link color="text">
                <div class="odr-block__info"
                     :class="{'odr-block__info_selected': model && model.length && selectedStyled}"
                     :style="`padding: ${padding} !important`"
                     :data-qa="dataQa + '.info'">
                    {{ info }}
                    <icon v-if="showArrow" size="s">keyboard_arrow_down</icon>
                </div>
            </text-link>
            <div slot="content" class="odr-block__content">
                <div v-if="hasSuggest" class="odr-block__suggest">
                    <Cinput
                        ref="suggest"
                        v-model="suggest_input"
                        icon="search"
                        :data-qa="dataQa + '.suggest'"
                        :placeholder="$trl('aw.to_search')"
                        :focused="suggestFocused"
                        @finished="$emit('finished', id)"
                        @focus="suggestFocused = true"
                        @blur="suggestFocused = false"/>
                    <linearProgress v-if="suggest_fetchingSuggest"/>
                    <gap v-if="options.length === 0 && !suggest_fetchingSuggest" bottom="single">
                        {{ $trl('aw.no_found') }}
                    </gap>
                </div>

                <component :is="'C' + type"
                           v-model="model"
                           v-bind="properties"
                           :adaptive="eAdaptive"
                           :account="account"
                           :data-qa="dataQa"
                           :state-id="stateId"
                           :focused="focused"
                           @invalid="(value) => $emit('invalid', value)"
                           @valid="(value) => $emit('valid', value)"
                           @focus="$emit('focus')"
                           @blur="$emit('blur')"
                           @finished="$emit('finished', id)"/>
            </div>
        </dropdown>
    </div>
    <span v-else>
        <div v-if="hasTitle"
             :class="{
                 'odr-block__wrap': asField,
             }">
            <span class="odr-message odr-message_small"
                  :class="{
                      'odr-message_color-main': hightlightTitle && (suggestFocused || focused) && !hasLargeTitle,
                      'odr-message_large': hasLargeTitle
                  }">
                <tpl :content="title"/>
                <text-regular color="error" v-if="$store.state.local.showBlockIds">Block id: {{id}}</text-regular>
            </span>
        </div>
        <text-regular v-else-if="$store.state.local.showBlockIds" color="error">Block id: {{id}}</text-regular>

        <div v-if="hasSuggest"
             class="odr-block__suggest odr-block__suggest_question">
            <Cinput
                ref="suggest"
                v-model="suggest_input"
                icon="search"
                :data-qa="dataQa + '.suggest'"
                :placeholder="$trl('aw.to_search')"
                :focused="suggestFocused"
                @finished="$emit('finished', id)"
                @focus="suggestFocused = true"
                @blur="suggestFocused = false"/>
            <linearProgress v-if="suggest_fetchingSuggest"/>
            <span v-if="options.length === 0 && !suggest_fetchingSuggest">
                {{ $trl('aw.no_found') }}
            </span>
        </div>
        <span :class="{
                  'odr-block__wrap': hasFieldWrap || !title,
                  'odr-block__wrap_no-title': !hasFieldWrap && !title,
                  'odr-block__wrap_with-close-title': hasFieldWrap && hasCloseTitle,
                  'odr-block__wrap_with-short-title': hasFieldWrap && hasShortTitle,
              }"
              :style="`padding: ${padding} !important`">
            <component :is="'C' + type"
                       :id="id"
                       :blockId="id"
                       :step-fields-count="stepFieldsCount"
                       v-model="model"
                       :adaptive="properties.adaptive"
                       v-bind="properties"
                       :account="account"
                       :data-qa="dataQa"
                       :state-id="stateId"
                       :focused="focused"
                       @focus="$emit('focus')"
                       @blur="$emit('blur')"
                       @finished="$emit('finished', id)"/>
        </span>
    </span>
</block-wrap>
</template>

<script>
import '@/scss/blocks/message.scss';
import '@/scss/blocks/block.scss';

import dropdown from '@/vue/components/dropdown.vue';
import linearProgress from '@/vue/markup/linear-progress.vue';
import suggestMixin from '@/utils/mixins/suggest';
import orderMixin from '@/utils/mixins/order';
import { showDropdown } from '@/utils/dropdown';
import { mapState } from 'vuex';

export default {
    mixins: [suggestMixin, orderMixin],
    props: {
        value: {},
        settings: {
            type: Object,
            default () { return {}; },
        },
        values: {},
        id: {
            type: String,
        },
        type: {
            required: true,
            type: String,
        },
        title: {
            type: String,
        },
        stateId: {
            type: String,
            required: true,
        },
        dropdown: {},
        asField: {
            type: Boolean,
            default: false,
        },
        showRequiredMark: {},
        dropdownShift: {
            type: String,
            default: '15',
        },
        showArrow: {
            type: Boolean,
            default: true,
        },
        padding: {
            type: String,
            default: '0px',
        },
        selectedStyled: {
            type: Boolean,
            default: true,
        },
        closeTitle: {
            type: Boolean,
            default: false,
        },
        parentTakeWidth: {},
        focused: {},
        focusable: {},
        stepFieldsCount: {},
    },
    data () {
        return {
            model: this.value,
            eDropdown: this.dropdown || this.settings.dropdown,
            dataQa: this.type + '.' + this.id,
            suggestFocused: false,
        };
    },
    watch: {
        focused () {
            this.$emit(this.focused ? 'focus' : 'blur');
            if (this.eDropdown) {
                showDropdown(this.dataQa);
            }
            if (this.hasSuggest && this.$refs.suggest) {
                this.suggestFocused = true;
            }
        },
        options () {
            if (this.$refs.dropdown) {
                this.$nextTick(() => {
                    this.$refs.dropdown.$emit('contentChanged');
                });
            }
        },
        model () {
            if (this.model instanceof Array) {
                if (this.value === null || this.value.join('') !== this.model.join('')) {
                    this.$emit('input', this.model);
                    return;
                }
            }

            if (this.model !== this.value) {
                this.$emit('input', this.model);
            }
        },
        value () {
            if (this.model !== this.value) {
                this.model = this.value;
            }
        },
    },
    mounted () {
        this.suggest_fetch(this.suggest_input);
    },
    computed: {
        ...mapState('dimensions', {
            dimensionsWidth: 'width',
        }),
        eAdaptive () {
            return !this.properties.highlight && this.adaptive;
        },
        adaptiveStart () {
            return this.properties.adaptive && this.properties.adaptiveStart;
        },
        adaptiveEnd () {
            return this.properties.adaptive && this.properties.adaptiveEnd;
        },
        transparent () {
            return this.properties.adaptive && ['address'].includes(this.type);
        },
        inlineBlock () {
            // step.vue/@oneColumnLayout value duplicated
            return ['input', 'textarea', 'combobox', 'time', 'datesimple', 'address', 'addressSearch'].includes(this.type);
        },
        adaptive () {
            return this.properties.adaptive && this.inlineBlock;
        },
        transparentMark () {
            return [].includes(this.type);
        },
        hightlightTitle () {
            return !['uploadImage'].includes(this.type);
        },
        properties () {
            return {
                id: this.id,
                title: this.title,
                values: this.options,
                allValues: this.allValues,
                dataQa: this.dataQa,
                ...this.settings,
            };
        },

        hasTitle () {
            return !!this.title && !['title', 'message', 'checkbox', 'address'].includes(this.type);
        },
        hasLargeTitle () {
            return !!this.title && ['linemenu', 'linemenuList', 'boxmenu'].includes(this.type);
        },
        hasCloseTitle () {
            return !!this.closeTitle ||
                       !!this.title && ['input', 'textarea', 'datesimple', 'combobox', 'time', 'uploadImage', 'signature', 'addressSearch'].includes(this.type) ||
                       ['checkbox'].includes(this.type) && !this.properties.highlight;
        },
        hasShortTitle () {
            return !!this.title && ['radio', 'checkboxList'].includes(this.type);
        },

        takeWidth () {
            if (['datesimple'].includes(this.type) && this.properties.hideDay) {
                return 9;
            }
            return this.properties.takeWidth;
        },
        hasSuggest () {
            return (this.settings.suggest || this.suggest) &&
                   this.eValues instanceof Array &&
                   !['combobox', 'input', 'addressSearch'].includes(this.type);
        },
        suggest_disabled () {
            return !this.hasSuggest;
        },
        hasDropdown () {
            return this.eDropdown && this.eValues instanceof Array && !['combobox'].includes(this.type);
        },
        hasFieldWrap () {
            return this.asField && !(['linemenu', 'linemenuList', 'address', 'autoGeoLocation', 'datesimple'].includes(this.type) && !this.eDropdown);
        },
        info () {
            const MAX_LENGTH = 30;
            const JOINER = ', ';

            const selectAllOption = this.allValues.find((item) => item.selectAllOption)
            if (this.model === null || (this.model instanceof Array && this.model.length === 0)) {
                if (selectAllOption) {
                    return selectAllOption.title || selectAllOption.description || selectAllOption.match || ''
                }
                if (this.values instanceof Array) {
                    let nullOption = this.allValues.find(item => item.id === null);
                    return nullOption ? (nullOption.title || nullOption.description || nullOption.match) : this.title;
                } else {
                    return this.title;
                }
            }

            if (this.model instanceof Array && this.values instanceof Array) {
                let info = this.model.filter((item) => item !== selectAllOption?.id).map(id => {
                    let option = this.allValues.find(item => item.id === id);
                    return (option.title || option.description || option.match);
                });

                function filterFittedItems (info, maxLength) {
                    let currentLength = 0;
                    return info.filter(title => {
                        currentLength += title.length;
                        return currentLength < maxLength;
                    });
                }
                let fitted = filterFittedItems(info, MAX_LENGTH);

                if (fitted.length === info.length) {
                    return fitted.join(JOINER);
                } else if (fitted.length === 0 && info.length === 1) {
                    return info[0].slice(0, MAX_LENGTH - 2) + '...';
                } else if (fitted.length - info.length === -1) {
                    let lastItem = info[info.length - 1];

                    return `${fitted.join(JOINER)}, ${lastItem[0]}...`;
                } else {
                    let moreLength = this.$trl('aw.and_n_more', 0).length;
                    let fittedWithMore = filterFittedItems(info, MAX_LENGTH - moreLength);
                    if (fittedWithMore.length === 0) {
                        return this.$trl('aw.n_selected', info.length);
                    } else {
                        return `${fittedWithMore.join(JOINER)} ${this.$trl('aw.and_n_more', info.length - fitted.length)}`;
                    }
                }
            }

            if (this.values instanceof Array) {
                let matchedValue = this.allValues.find(item => item.id === this.model);
                return matchedValue ? matchedValue.title : this.model;
            }

            return this.model;
        },
        options () {
            return this.order_options(this.suggest_options(this.eValues));
        },
        allValues () {
            return this.suggest_allValues(this.eValues);
        },
        eValues () {
            let values = typeof (this.values) === 'string' ? JSON.parse(this.values) : this.values;
            if (values instanceof Array) {
                return this.values.map(value => {
                    if (value.id === undefined) {
                        value.id = null;
                    }

                    return value;
                });
            } else {
                return values;
            }
        },
    },
    beforeCreate () {
        // beforeCreate is to manage circular dependencies
        this.$options.components.Clinemenu = require('@/vue/blocks/linemenu.vue').default;
        this.$options.components.Caddress = require('@/vue/blocks/address.vue').default;
        this.$options.components.Cboxmenu = require('@/vue/blocks/boxmenu.vue').default;
        this.$options.components.Ccheckbox = require('@/vue/blocks/checkbox.vue').default;
        this.$options.components.CcheckboxList = require('@/vue/blocks/checkbox-list.vue').default;
        this.$options.components.Ccombobox = require('@/vue/blocks/combobox.vue').default;
        this.$options.components.Cdate = require('@/vue/blocks/calendar.vue').default;
        this.$options.components.Cdatesimple = require('@/vue/blocks/datesimple.vue').default;
        this.$options.components.Cinput = require('@/vue/blocks/input.vue').default;
        this.$options.components.ClinemenuList = require('@/vue/blocks/linemenu-list.vue').default;
        this.$options.components.Cmessage = require('@/vue/blocks/message.vue').default;
        this.$options.components.Coptions = require('@/vue/blocks/options.vue').default;
        this.$options.components.Cradio = require('@/vue/blocks/radio.vue').default;
        this.$options.components.Csignature = require('@/vue/blocks/signature.vue').default;
        this.$options.components.Ctime = require('@/vue/blocks/time.vue').default;
        this.$options.components.Ctitle = require('@/vue/blocks/title.vue').default;
        this.$options.components.CuploadImage = require('@/vue/blocks/upload-image.vue').default;
        this.$options.components.CuploadFile = require('@/vue/blocks/upload-file.vue').default;
        this.$options.components.Ctextarea = require('@/vue/blocks/textarea.vue').default;
        this.$options.components.Cswipecard = require('@/vue/blocks/swipecard.vue').default;
        this.$options.components.CautoGeoLocation = require('@/vue/blocks/autogeolocation.vue').default;
        this.$options.components.Ciframe = require('@/vue/blocks/iframe.vue').default;
        this.$options.components.CaddressSearch = require('@/vue/blocks/address-search.vue').default;
    },
    components: {
        linearProgress,
        Cdocumentation: () => import(/* webpackChunkName: "documentation" */ '@clearwave/odoro-markup/lib/vue/documentation.vue'),
        tpl: require('@/vue/markup/tpl.vue').default,
        'block-wrap': require('@/vue/block-wrap.vue').default,
        'text-regular': require('@clearwave/odoro-markup/lib/vue/text-regular.vue').default,
        icon: require('@clearwave/odoro-markup/lib/vue/icon.vue').default,
        'text-link': require('@clearwave/odoro-markup/lib/vue/text-link.vue').default,
        gap: require('@clearwave/odoro-markup/lib/vue/gap.vue').default,
        dropdown,
    },
};
</script>
